import React from 'react';

/**
 * ErrorBoundary — catches ChunkLoadErrors (network/lazy-load failures) and
 * other runtime errors without showing the ugly CRA overlay.
 *
 * • ChunkLoadError  → auto-reload once, then show a friendly "reconnecting" UI
 * • Other errors    → show a clean error card (no stack trace exposed to users)
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, reloaded: false };
  }

  static getDerivedStateFromError(error) {
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk .+ failed/i.test(error?.message || '') ||
      /loading CSS chunk/i.test(error?.message || '');

    return { hasError: true, isChunkError };
  }

  componentDidCatch(error, info) {
    // Only log non-chunk errors — chunk errors are just network blips
    if (!this.state.isChunkError) {
      console.error('[ErrorBoundary]', error, info);
    }

    if (this.state.isChunkError && !this.state.reloaded) {
      // Mark that we've tried reloading so we don't loop forever
      this.setState({ reloaded: true });
      // Small delay so the state update commits before reload
      setTimeout(() => window.location.reload(), 300);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, isChunkError: false, reloaded: false });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.isChunkError) {
      // Auto-reload is in progress — show a subtle spinner
      return (
        <div style={styles.center}>
          <div style={styles.card}>
            <div style={styles.spinner} />
            <p style={styles.title}>Reconnecting…</p>
            <p style={styles.sub}>Please wait while the page reloads.</p>
          </div>
        </div>
      );
    }

    // Generic error fallback
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <div style={styles.icon}>⚠️</div>
          <p style={styles.title}>Something went wrong</p>
          <p style={styles.sub}>An unexpected error occurred. Please try again.</p>
          <button style={styles.btn} onClick={this.handleRetry}>Try Again</button>
        </div>
      </div>
    );
  }
}

const styles = {
  center: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', background: '#f5f5f0',
  },
  card: {
    background: '#fff', borderRadius: 16, padding: '40px 32px',
    textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    maxWidth: 360, width: '90%',
  },
  icon: { fontSize: 40, marginBottom: 12 },
  title: { fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 8px' },
  sub: { fontSize: 14, color: '#6B7280', margin: '0 0 20px' },
  btn: {
    background: '#1a3a5c', color: '#fff', border: 'none',
    borderRadius: 10, padding: '10px 24px', fontSize: 14,
    fontWeight: 600, cursor: 'pointer',
  },
  spinner: {
    width: 36, height: 36, borderRadius: '50%',
    border: '3px solid #E5E7EB', borderTopColor: '#1a3a5c',
    animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
  },
};

// Inject keyframe for spinner (once)
if (typeof document !== 'undefined' && !document.getElementById('eb-spin')) {
  const style = document.createElement('style');
  style.id = 'eb-spin';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

export default ErrorBoundary;
